import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import useAlert from "../../../hooks/useAlert";
import AlertLevel from "../../../models/alert";
import ecgsService from "../../../services/ecgs.service";
import AlertModal, {
  AlertModalProps,
} from "../../Shared/AlertModal/AlertModal";

const MILLISECONDS_BEFORE_TIMEOUT_WARNING = 60000;

type EcgReviewLockProps = {
  ecgId: string;
  timeoutInMilliseconds: number;
  onLockTimeout?: () => void;
  onLock?: () => void;
};

const EcgReviewLock = ({
  ecgId,
  timeoutInMilliseconds,
  onLock,
  onLockTimeout,
}: EcgReviewLockProps) => {
  const [loading, setLoading] = useState(false);
  const [showReviewTimeout, setShowReviewTimeout] = useState(false);
  const [showReviewTimeoutWarning, setShowReviewTimeoutWarning] = useState(
    false
  );

  const reviewTimeoutsRef = useRef<{
    reviewEndId: number | undefined;
    reviewWarningId: number | undefined;
  }>({
    reviewEndId: undefined,
    reviewWarningId: undefined,
  });

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const initTimeouts = useCallback((timeoutInMilliseconds: number) => {
    setReviewTimeoutWarning(timeoutInMilliseconds);
    setReviewTimeout(timeoutInMilliseconds);

    if (onLock) onLock();
  }, []); // eslint-disable-line

  useEffect(() => {
    const {
      reviewEndId,
      reviewWarningId: reviewReminderId,
    } = reviewTimeoutsRef.current;

    return () => {
      // On unmount clear timeouts
      clearTimeout(reviewEndId);
      clearTimeout(reviewReminderId);
    };
  }, []);

  useEffect(() => {
    if (!ecgId || !timeoutInMilliseconds) {
      return;
    }

    if (ecgId && timeoutInMilliseconds > 1) {
      initTimeouts(timeoutInMilliseconds);
    }
  }, [ecgId, timeoutInMilliseconds, initTimeouts]);

  const setReviewTimeout = (timeoutInMilliseconds: number) => {
    clearTimeout(reviewTimeoutsRef.current.reviewEndId);

    reviewTimeoutsRef.current.reviewEndId = window.setTimeout(() => {
      setShowReviewTimeoutWarning(false);
      setShowReviewTimeout(true);
    }, timeoutInMilliseconds);
  };

  const setReviewTimeoutWarning = (timeoutInMilliseconds: number) => {
    clearTimeout(reviewTimeoutsRef.current.reviewWarningId);

    const warningTimeoutInMilliseconds =
      timeoutInMilliseconds - MILLISECONDS_BEFORE_TIMEOUT_WARNING;

    if (warningTimeoutInMilliseconds <= 0) {
      return;
    }

    reviewTimeoutsRef.current.reviewWarningId = window.setTimeout(() => {
      setShowReviewTimeoutWarning(true);
    }, warningTimeoutInMilliseconds);
  };

  const lockEcg = async (): Promise<boolean> => {
    const {
      success,
      data: newTimeoutInMilliseconds,
      errorCode,
    } = await ecgsService.lockEcg(ecgId!);
    const { message, support } = getAlertMessageAndSupportContent(
      "ERROR.ECG_LOCK.EXTEND_SESSION",
      "An error occurred while trying to extend review session.<br></br>Please try again.",
      {
        br: () => <br></br>,
      },
      errorCode
    );
    if (!success || !newTimeoutInMilliseconds) {
      showErrorAlert(message, support);
      return success;
    }

    initTimeouts(newTimeoutInMilliseconds);
    return success;
  };

  const extendTimeoutHandler = async () => {
    setLoading(true);
    const success = await lockEcg();
    setLoading(false);
    if (success) {
      setShowReviewTimeoutWarning(false);
    }
  };

  const handleReviewTimeout = async () => {
    setShowReviewTimeout(false);
    if (onLockTimeout) onLockTimeout();
  };

  return (
    <>
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>

      <ReviewTimeoutWarningModal
        secondsLeft={MILLISECONDS_BEFORE_TIMEOUT_WARNING / 1000}
        dialog
        level={AlertLevel.Warning}
        show={showReviewTimeoutWarning}
        loading={loading}
        okBtn={{
          text: <FormattedMessage id="GLOBAL.YES" defaultMessage="Yes" />,
          onClick: extendTimeoutHandler,
        }}
        cancelBtn={{
          text: <FormattedMessage id="GLOBAL.NO" defaultMessage="No" />,
        }}
        onClose={() => setShowReviewTimeoutWarning(false)}
      />

      <ReviewTimeoutModal
        level={AlertLevel.Info}
        show={showReviewTimeout}
        okBtn={{
          text: (
            <FormattedMessage id="GLOBAL.CONTINUE" defaultMessage="Continue" />
          ),
        }}
        onClose={handleReviewTimeout}
      />
    </>
  );
};

export default EcgReviewLock;

const ReviewTimeoutWarningModal = ({
  secondsLeft,
  ...modalProps
}: AlertModalProps & { secondsLeft: number }) => {
  const [seconds, setSeconds] = useState(secondsLeft);

  useEffect(() => {
    let displayTimerId: number;
    if (modalProps.show && seconds > 0) {
      displayTimerId = window.setTimeout(() => setSeconds(seconds - 1), 1000);
    }
    return () => clearTimeout(displayTimerId);
  }, [modalProps.show, seconds]);

  // Extract hh:mm format string
  const hhmm = new Date(seconds * 1000).toISOString().substring(14, 19);

  return (
    <AlertModal {...modalProps}>
      <FormattedMessage
        id="ECG.REVIEW.TIMEOUT_WARNING"
        defaultMessage={
          "Your review time for this ECG will expire soon. Do you need more time to complete the review?"
        }
      />
      <p>{hhmm}</p>
    </AlertModal>
  );
};

const ReviewTimeoutModal = (props: AlertModalProps) => {
  return (
    <AlertModal {...props}>
      <FormattedMessage
        id="ECG.REVIEW.TIMEOUT_END"
        defaultMessage={
          "Your review session for this ECG has ended. Press Continue to refresh ECG list"
        }
      />
    </AlertModal>
  );
};
